// analytics.js — the single analytics module for www.coinversa.ai.
//
// Owns: PostHog config and consent lifecycle (grant, re-grant, withdraw), the
// event envelope, the `before_send` scrubber hookup, the X pixel gate,
// first-touch persistence, and the `cta_clicked` + signup-link helper.
// The Explorer's src/explorer/lib/analytics.js re-exports from here.
//
// Contract: /docs/analytics/CONTRACT.md (portal repo) §3, §4, §5.1, §7, §8, §9.
// Every function here swallows its own errors: analytics never breaks product.

import posthog from 'posthog-js';
import { getConsent, hasConsent } from './consent';
import { beforeSend, normalizeCtaId } from './sanitize';
import {
  capturePageTouch,
  persistTouch,
  clearTouches,
  getFirstTouch,
  getPageTouch,
} from './attribution';
import { developerSignupUrl, PLANS, SURFACES } from './developerPortal';
import { loadXPixel, disableXPixel, clearXPixelCookies } from './xPixel';

export const SCHEMA_VERSION = 1;
export const APP = 'www';
const ENVIRONMENTS = ['production', 'staging', 'development', 'test'];

export function analyticsEnvironment(raw = process.env.REACT_APP_ANALYTICS_ENV) {
  const value = String(raw || '').trim().toLowerCase();
  return ENVIRONMENTS.includes(value) ? value : 'development';
}

export function envelope(environment = analyticsEnvironment()) {
  return { schema_version: SCHEMA_VERSION, source: 'frontend', app: APP, environment };
}

// Never initialise from a local origin. `posthog.init` is what turns on
// capture_pageview and autocapture, so without this guard every dev server
// writes $pageview/$autocapture into the *production* project. Deliberately
// NOT gated on NODE_ENV: a local production build (`serve -s build`) is still
// local traffic. Staging/preview deploys are real hosts and keep tracking
// (tagged by `environment`).
export const LOCAL_HOSTNAMES = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[?::1\]?)$|\.localhost$|\.local$/i;

export function isLocalOrigin(loc = typeof window !== 'undefined' ? window.location : null) {
  const host = loc?.hostname || '';
  return LOCAL_HOSTNAMES.test(host);
}

// Longer than posthog-js's persistence_save_debounce_ms (250 ms under
// defaults '2026-05-30').
const LATE_CLEAR_MS = 600;

const DEFAULT_KEY = 'phc_yaGEw3rQWMkSNsrxBkdx2trUYeaRdqugzYhcddLfzQQ9';

/**
 * Query parameters whose VALUES PostHog must not keep in its own persistence
 * (§4: click-id values are never stored). The SDK stores the raw landing URL
 * ($initial_person_info, $client_session_props) and the campaign params in
 * localStorage, sessionStorage and the cross-subdomain ph_* cookie. With
 * mask_personal_data_properties the SDK replaces the values of its built-in
 * click-id list (gclid, fbclid, twclid, msclkid, ...) with '<masked>'; these
 * are added on top. Same list as the portal's PERSONAL_DATA_PARAMS.
 * before_send still reduces every URL that is SENT (§7); this covers what is
 * STORED.
 */
export const PERSONAL_DATA_PARAMS = [
  'phid', 'api_key', 'apikey', 'key', 'x-api-key', 'token', 'access_token', 'refresh_token',
  'id_token', 'code', 'state', 'email', 'password', 'secret', 'signature', 'session_id',
  'privy_oauth_code', 'privy_oauth_state', 'privy_oauth_provider', 'privy_access_token',
];

// The only query params whose values the SDK may store as-is (§4 allowlist).
const STORABLE_PARAMS = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']);

/**
 * PERSONAL_DATA_PARAMS plus every other parameter name on the given URL that
 * is not a §4 UTM key, spelled exactly as it appears (the SDK matches raw,
 * case-sensitive names). Computed when the SDK initialises, which is when it
 * records the landing URL, so an ad landing URL carrying `email=`, `name=` or
 * any unknown param is stored with those values masked.
 */
export function personalDataParams(search = typeof window !== 'undefined' ? window.location.search : '') {
  const names = new Set(PERSONAL_DATA_PARAMS);
  try {
    String(search || '').replace(/^\?/, '').split('&').forEach((pair) => {
      const name = pair.split('=')[0];
      if (name && !STORABLE_PARAMS.has(name)) names.add(name);
    });
  } catch {
    /* keep the static list */
  }
  return [...names];
}

/** Reduce a referrer to `<scheme>://<host>/`, the browser's own strict-origin form. */
export function referrerOrigin(raw) {
  if (typeof raw !== 'string' || !raw) return '';
  try {
    const url = new URL(raw);
    if (url.protocol === 'http:' || url.protocol === 'https:') return `${url.origin}/`;
    // e.g. android-app://com.google.android.googlequicksearchbox/
    return url.host ? `${url.protocol}//${url.host}/` : '';
  } catch {
    return '';
  }
}

const maskedDocs = typeof WeakSet === 'function' ? new WeakSet() : null;

/**
 * posthog-js reads `document.referrer` directly and stores it raw (query
 * included) in $initial_person_info.r, $client_session_props and $referrer,
 * and derives `ph_keyword` (the visitor's search query) from its q/p param.
 * No SDK option masks the referrer. Browsers already send only the origin
 * cross-origin by default, but a same-origin reload or a referring site with a
 * permissive Referrer-Policy hands over the full URL. So, once consent is
 * granted and before the SDK starts, this page's `document.referrer` is
 * reduced to its origin. Every other reader on www only needs the host
 * (attribution.js keeps `referrer_host`). $search_engine still resolves from
 * the origin; ph_keyword no longer can.
 */
export function maskDocumentReferrer(doc = typeof document !== 'undefined' ? document : null) {
  try {
    if (!doc || (maskedDocs && maskedDocs.has(doc))) return;
    let desc = Object.getOwnPropertyDescriptor(doc, 'referrer');
    for (let proto = Object.getPrototypeOf(doc); !desc && proto; proto = Object.getPrototypeOf(proto)) {
      desc = Object.getOwnPropertyDescriptor(proto, 'referrer');
    }
    if (!desc) return;
    const read = () => (desc.get ? desc.get.call(doc) : desc.value);
    Object.defineProperty(doc, 'referrer', {
      configurable: true,
      enumerable: true,
      get: () => referrerOrigin(read()),
    });
    if (maskedDocs) maskedDocs.add(doc);
  } catch {
    /* leave the referrer alone; before_send still reduces what is sent */
  }
}

/**
 * before_send step that guarantees the §5 envelope on every event, including
 * the SDK's own initial $pageview, independent of register() timing.
 *
 * The envelope wins over event properties. Several legacy explorer_* events
 * used `source` for the on-page placement, which collides with the envelope's
 * `source: 'frontend'`; call sites now send `placement`, and any stray
 * non-envelope `source` is moved to `placement` here rather than lost.
 */
export function withEnvelope(environment = analyticsEnvironment()) {
  const env = envelope(environment);
  return (event) => {
    if (!event || typeof event !== 'object') return event;
    const props = { ...(event.properties || {}) };
    if (props.source != null && props.source !== env.source && props.placement == null) {
      props.placement = props.source;
    }
    return { ...event, properties: { ...props, ...env } };
  };
}

/** PostHog init options. Exported for the config test. */
export function buildPosthogConfig({
  host = process.env.REACT_APP_POSTHOG_HOST,
  replay = process.env.REACT_APP_POSTHOG_REPLAY,
  environment = analyticsEnvironment(),
} = {}) {
  const replayOn = String(replay || '') === '1';
  const config = {
    // Send straight to PostHog US Cloud. This used to default to the
    // first-party proxy https://ph.coinversa.ai, which answered without CORS
    // for months and caused 100% event loss (commit 0cfd7e7). Do not point
    // this at a proxy again without checking `/array/<key>/config` returns 200
    // *with* access-control-allow-origin from a real browser origin.
    api_host: host || 'https://us.i.posthog.com',
    // §9: posthog-js 1.390.2 (package-lock, what CI ships) accepts
    // '2026-05-30' (ConfigDefaults = '2026-05-30' | '2026-01-30' | ...). It
    // enables history_change pageviews, persistence_save_debounce_ms 250,
    // split_storage and detect_google_search_app. capture_pageview is ALSO set
    // explicitly so pageviews never depend on the defaults string.
    defaults: '2026-05-30',
    capture_pageview: 'history_change',
    capture_pageleave: 'if_capture_pageview',
    cross_subdomain_cookie: true,
    persistence: 'localStorage+cookie',
    // Mask click ids and PERSONAL_DATA_PARAMS in the URLs the SDK persists
    // (the ph_* cookie rides on every *.coinversa.ai request). grant() adds
    // the landing URL's other non-UTM param names at init time.
    mask_personal_data_properties: true,
    custom_personal_data_properties: [...PERSONAL_DATA_PARAMS],
    // No identify() in this repo; 'always' would bill every anonymous visitor
    // at the identified-event rate. The portal identifies with the Privy DID
    // and the shared .coinversa.ai cookie carries the journey.
    person_profiles: 'identified_only',
    internal_or_test_user_hostname: LOCAL_HOSTNAMES,
    // Envelope first, then the §7 scrubber (which fails closed).
    before_send: [withEnvelope(environment), beforeSend],
    autocapture: {
      element_allowlist: ['a', 'button', 'form', 'input', 'select', 'textarea', 'label'],
      capture_copied_text: false,
    },
    mask_all_element_attributes: false,
    mask_all_text: false,
    // network timing carries raw request URLs.
    capture_performance: { network_timing: false },
    disable_session_recording: !replayOn,
  };
  if (replayOn) {
    config.session_recording = {
      maskAllInputs: true,
      maskTextSelector: '.ph-mask, code, pre, [data-ph-mask]',
    };
  }
  return config;
}

// ph_<token>_posthog (cookie and localStorage) and __ph_opt_in_out_* are the
// SDK's identifier/opt state. Clear every copy on the exact host and the apex.
export function clearPostHogStorage(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return;
  const isPh = (name) => name.startsWith('ph_') || name.startsWith('__ph_opt_in_out');
  try {
    const names = win.document.cookie
      .split('; ')
      .map((row) => row.split('=')[0])
      .filter(isPh);
    const domains = ['', '; domain=.coinversa.ai', `; domain=${win.location.hostname}`];
    names.forEach((name) => {
      domains.forEach((domain) => {
        win.document.cookie = `${name}=; path=/; max-age=0${domain}`;
      });
    });
  } catch {
    /* cookies unavailable; nothing to clear */
  }
  for (const store of ['localStorage', 'sessionStorage']) {
    try {
      const s = win[store];
      const keys = [];
      for (let i = 0; i < s.length; i += 1) {
        const k = s.key(i);
        if (k && isPh(k)) keys.push(k);
      }
      keys.forEach((k) => s.removeItem(k));
    } catch {
      /* storage blocked */
    }
  }
}

/**
 * The consent lifecycle as a small state machine, with every side effect
 * injected so the tests can drive it with a fake SDK.
 */
export function createAnalyticsController({
  client = posthog,
  token = process.env.REACT_APP_POSTHOG_KEY || DEFAULT_KEY,
  environment = analyticsEnvironment(),
  config = buildPosthogConfig({ environment }),
  isLocal = isLocalOrigin,
  pixel = { load: loadXPixel, disable: disableXPixel, clearCookies: clearXPixelCookies },
  touches = { persist: persistTouch, clear: clearTouches },
  clearStorage = clearPostHogStorage,
  maskReferrer = maskDocumentReferrer,
  currentSearch = () => (typeof window !== 'undefined' ? window.location.search : ''),
} = {}) {
  let started = false;
  let capturing = false;
  let lateClear = null;

  // Our own kill switch, first in before_send. Withdrawal deletes every ph_*
  // and __ph_opt_in_out_* key — including the SDK's opt-out marker, which
  // lives in localStorage — and posthog-js 1.390.2 then reports
  // has_opted_out_capturing() === false again (verified in
  // analytics.sdk.test.js). Storing nothing and still sending nothing needs a
  // gate the SDK cannot forget. Fails closed.
  const gate = (event) => (capturing ? event : null);
  const gatedConfig = {
    ...config,
    before_send: [gate, ...[].concat(config.before_send || [])],
  };

  const safe = (fn) => {
    try {
      return fn();
    } catch {
      return undefined;
    }
  };

  function grant({ surface, origin } = {}) {
    if (isLocal()) return; // never on local origins, no pixel either
    const wasCapturing = capturing;
    try {
      clearTimeout(lateClear);
    } catch {
      /* no-op */
    }
    lateClear = null;
    // Open the gate first: opt_in_capturing() captures the pageview for the
    // current page synchronously.
    capturing = true;
    // Before the SDK can read the referrer (init records it on the first
    // capture, which opt-in/init trigger synchronously).
    safe(() => maskReferrer());
    if (!started && !client.__loaded) {
      started = true;
      safe(() => client.init(token, {
        ...gatedConfig,
        custom_personal_data_properties: [...new Set([
          ...[].concat(gatedConfig.custom_personal_data_properties || []),
          ...personalDataParams(currentSearch()),
        ])],
      }));
    } else if (!wasCapturing) {
      // Also covers an SDK that is already __loaded (hot reload) — §9.
      started = true;
      // Re-grant in the same page view (the bug this replaces: init was
      // skipped because it had already run, and nothing opted back in).
      safe(() => client.set_config({ persistence: 'localStorage+cookie' }));
      safe(() => client.opt_in_capturing({ captureEventName: false }));
    }
    // Registered on every grant: super properties survive the persistence
    // switch, but re-registering is cheap and makes grant idempotent.
    safe(() => client.register(envelope(environment)));
    safe(() => touches.persist());
    safe(() => pixel.load());
    if (!wasCapturing && origin === 'local' && (surface === 'banner' || surface === 'settings')) {
      safe(() => client.capture('consent_changed', { consent_state: 'granted', surface }));
    }
  }

  function withdraw() {
    // Emit nothing: close the gate before anything else can capture. Then,
    // per contract §3: opt out, drop to memory persistence (so a queued save
    // cannot rewrite the cookie), clear, stop replay. Deliberately NOT
    // posthog.reset(): it mints a fresh distinct_id and writes the identifier
    // straight back.
    const wasCapturing = capturing;
    capturing = false;
    if (started && wasCapturing) {
      safe(() => client.opt_out_capturing());
      // defaults '2026-05-30' debounces persistence writes by 250 ms. The
      // switch to memory replaces the sessionStorage persistence object, and a
      // save still pending on the old one would write ph_<token>_posthog back
      // into sessionStorage after the clear below (verified in
      // analytics.sdk.scrub.test.js). Land pending writes now so the clear
      // removes them.
      safe(() => client.persistence && client.persistence.flush && client.persistence.flush());
      safe(() => client.sessionPersistence && client.sessionPersistence.flush
        && client.sessionPersistence.flush());
      safe(() => client.set_config({ persistence: 'memory' }));
    }
    safe(() => clearStorage());
    // The SDK object that the memory switch replaced can still flush one
    // debounced write after this point (observed: an empty ph_<token>_posthog
    // entry in sessionStorage ~250 ms later). Clear once more after the
    // debounce window, unless consent was granted again in the meantime.
    if (started) {
      try {
        clearTimeout(lateClear);
        lateClear = setTimeout(() => {
          lateClear = null;
          if (!capturing) safe(() => clearStorage());
        }, LATE_CLEAR_MS);
      } catch {
        /* timers unavailable */
      }
    }
    if (started) safe(() => client.stopSessionRecording());
    safe(() => pixel.disable());
    safe(() => touches.clear());
  }

  // No consent at page load: remove anything left from an earlier consented
  // visit that this page never got to withdraw itself — a withdrawal made on
  // developers.coinversa.ai while www was closed (it can only clear the shared
  // .coinversa.ai cookies, not www's own localStorage/sessionStorage), or an
  // expired/denied cv_consent cookie. Removal only: the SDK was never
  // initialised on this page and is not touched, nothing is written, nothing
  // is emitted. Mirrors the portal's analyticsRuntime start().
  function cleanupWithoutConsent() {
    safe(() => clearStorage());
    safe(() => touches.clear());
    safe(() => pixel.clearCookies && pixel.clearCookies());
  }

  /** Apply a consent state. `origin`: 'startup' | 'local' | 'sync'. */
  function apply(state, { surface, origin = 'local' } = {}) {
    if (state === 'granted') grant({ surface, origin });
    else if (origin !== 'startup') withdraw();
    else if (!started) cleanupWithoutConsent();
  }

  return {
    apply,
    isCapturing: () => capturing,
    isStarted: () => started,
  };
}

let controller = null;

/** Wire the real SDK. Called once from src/index.js. */
export function initAnalytics() {
  if (controller) return controller;
  controller = createAnalyticsController();
  safeCapturePageTouch();
  controller.apply(getConsent(), { origin: 'startup' });
  return controller;
}

export function applyConsent(state, meta) {
  try {
    (controller || initAnalytics()).apply(state, meta);
  } catch {
    /* no-op */
  }
}

function safeCapturePageTouch() {
  try {
    capturePageTouch();
  } catch {
    /* no-op */
  }
}

function readyClient(client = posthog) {
  try {
    if (controller && client === posthog && !controller.isCapturing()) return null;
    if (!client || !client.__loaded || typeof client.capture !== 'function') return null;
    if (typeof client.has_opted_out_capturing === 'function' && client.has_opted_out_capturing()) {
      return null;
    }
    return client;
  } catch {
    return null;
  }
}

/**
 * Fire an event. Never throws; no-ops before consent (events fired before
 * consent are dropped, not queued). The envelope is registered as super
 * properties on grant.
 */
export function captureAnalyticsEvent(event, properties = {}, client = posthog) {
  try {
    const ph = readyClient(client);
    if (!ph) return false;
    ph.capture(event, properties);
    return true;
  } catch {
    return false;
  }
}

const DESTINATIONS = ['portal_signup', 'portal', 'docs', 'mailto'];

/**
 * `cta_clicked` + the URL to navigate to, from one call so `cta_id` and
 * `cv_ref_src` can never drift apart (they did in V3Shell).
 *
 * The first-touch `cv_ft_*` params are added only if consent is granted at
 * click time.
 */
export function ctaClick(ctaId, {
  surface = 'website',
  destination = 'portal_signup',
  plan,
  href,
  client = posthog,
} = {}) {
  let url = href;
  try {
    const id = normalizeCtaId(ctaId);
    const safeSurface = SURFACES.includes(surface) ? surface : 'website';
    const dest = DESTINATIONS.includes(destination) ? destination : 'portal_signup';
    if (dest === 'portal_signup') {
      const firstTouch = hasConsent() ? (getFirstTouch() || getPageTouch()) : null;
      url = developerSignupUrl(id, { surface: safeSurface, plan, firstTouch });
    }
    if (id) {
      const props = { cta_id: id, surface: safeSurface, destination: dest };
      if (PLANS.includes(plan)) props.plan = plan;
      captureAnalyticsEvent('cta_clicked', props, client);
    }
  } catch {
    /* fall through with whatever URL we have */
  }
  return url;
}

/**
 * Props for a CTA anchor. The rendered href carries only the internal
 * referral params (safe without consent). At click time the href is rebuilt
 * with consent-dependent first-touch params before the browser follows it.
 */
export function ctaLinkProps(ctaId, { surface = 'website', destination = 'portal_signup', plan, href, onClick } = {}) {
  const staticHref = destination === 'portal_signup'
    ? developerSignupUrl(ctaId, { surface, plan })
    : href;
  const handle = (event) => {
    const url = ctaClick(ctaId, { surface, destination, plan, href: staticHref });
    try {
      const el = event?.currentTarget;
      if (url && el && destination === 'portal_signup') {
        el.href = url;
        // The browser resolves the navigation URL during this click's default
        // action. Put the consent-free href back afterwards so a later
        // "open in new tab"/"copy link" (no click handler runs) never carries
        // cv_ft_* from an earlier, possibly withdrawn, consent.
        if (url !== staticHref) {
          setTimeout(() => {
            try {
              el.href = staticHref;
            } catch {
              /* no-op */
            }
          }, 0);
        }
      }
    } catch {
      /* keep the static href */
    }
  };
  return {
    href: staticHref,
    onClick: (event) => {
      handle(event);
      if (typeof onClick === 'function') onClick(event);
    },
    // Middle-click opens a tab without a `click` event.
    onAuxClick: (event) => {
      if (event?.button === 1) handle(event);
    },
  };
}
